import { useFormSyncState } from '@/hooks';
import { PATTERN_NAME } from '@/utils';
import { Form, Input, Select } from '@arco-design/web-react';
import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAllNamespaces } from '../../hooks';
import { toNamespaceOptions } from '../../utils';
import { BaseInfoData, useBaseInfoStore } from './useBaseInfoStore';

const FormItem = Form.Item;
const TextArea = Input.TextArea;

/**
 * 工作负载-基本信息表单
 */
export interface BaseInfoProps {
  style?: React.CSSProperties;
  cluster: string;
}

export function BaseInfo({ style, cluster }: BaseInfoProps) {
  const { t } = useTranslation();
  const msg = {
    name: t('common:name'),
    name_placeholder: t(
      'clusters:the.name.can.only.contain.lowercase.letters.numbers.and.hyphens.and.must.start.and.end.with.a.lowercase.letter.or.number',
    ),
    alias: t('common:nickname'),
    alias_placeholder: t(
      'clusters:please.enter.a.deployment.alias.which.can.contain.any.characters.up.to.63.characters.maximum',
    ),
    namespace: t('common:namespace'),
    namespace_placeholder: t('clusters:select.namespace.for.creating.resources'),
    annotations: t('common:description'),
    annotations_placeholder: t(
      'clusters:please.enter.a.deployment.description.which.can.contain.any.characters.up.to.256.characters.maximum',
    ),
  };
  const [form] = Form.useForm<BaseInfoData>();
  const { baseInfo, updateBaseInfo, setValidate } = useBaseInfoStore();
  useFormSyncState(form, baseInfo); // 同步在非表单组件更新状态的时候同步到表单回显
  const { data: namespaces, isLoading: isNamespacesloading } = useAllNamespaces(cluster);
  const [namespacesOptions, setNamespacesOptions] = React.useState<
    { label: string; value: string }[]
  >([]);
  useEffect(() => {
    if (namespaces) {
      setNamespacesOptions(toNamespaceOptions(namespaces));
    }
  }, [namespaces]);

  // 校验函数
  const validateFn = useCallback(async () => {
    try {
      await form.validate();
      return true;
    } catch (e) {
      return false;
    }
  }, [form]);

  useEffect(() => {
    setValidate(validateFn);
  }, [validateFn, setValidate]);

  return (
    <Form
      layout="vertical"
      style={style}
      form={form}
      initialValues={baseInfo}
      onValuesChange={(_, values) => {
        updateBaseInfo(values);
      }}
    >
      <FormItem
        label={msg.name}
        field="name"
        rules={[
          { required: true },
          {
            match: PATTERN_NAME,
            message: msg.name_placeholder,
          },
        ]}
      >
        <Input style={{ width: 600 }} autoComplete="off" placeholder={msg.name_placeholder} />
      </FormItem>
      <FormItem label={msg.alias} field="alias">
        <Input style={{ width: 600 }} autoComplete="off" placeholder={msg.alias_placeholder} />
      </FormItem>
      <FormItem label={msg.namespace} field="namespace" rules={[{ required: true }]}>
        <Select
          style={{ width: 600 }}
          placeholder={msg.namespace_placeholder}
          options={namespacesOptions}
          loading={isNamespacesloading}
          showSearch
        />
      </FormItem>
      <FormItem label={msg.annotations} field="annotations">
        <TextArea
          style={{ width: 600 }}
          autoComplete="off"
          autoSize={{ minRows: 2, maxRows: 6 }}
          placeholder={msg.annotations_placeholder}
        />
      </FormItem>
    </Form>
  );
}
